import {
  IBidResponse,
  IAuctionResults,
  IAuctionEnd
} from '../../typings/IPrebid'
import { IBidRequest } from '../../typings/IPrebidAdapter'
import { PubSub } from '../helpers/pubSub'
import type { Slot } from '../slots/Slot'
import IVideoConfig from '../../typings/IVideoConfig'
import {
  IReportingObserverReport,
  setUpGlobalListeners
} from './helpers/globalRegistration'
import { Metric } from 'web-vitals'

export enum Event {
  impressionViewable = 'impressionViewable',
  slotVisibilityChanged = 'slotVisibilityChanged',
  slotRenderEnded = 'slotRenderEnded',
  slotWrapperRenderEnded = 'slotWrapperRenderEnded',
  bidResponse = 'bidResponse',
  bidWon = 'bidWon',
  auctionEnd = 'auctionEnd',
  bidRequested = 'bidRequested',
  bidReady = 'bidReady', // Custom Event
  bidTimeout = 'bidTimeout',
  newRelicLoaded = 'newRelicLoaded',
  timeOnSite = 'timeOnSite',
  initLauncher = 'initLauncher',
  outstreamAdError = 'outstreamAdError',
  refreshSlot = 'refreshSlot', // Request to refresh a slot
  slotRefreshed = 'slotRefreshed', // Event to announce that a slot is being refreshed
  shortCircuitRefresh = 'shortCircuitRefresh',
  heavyAdRemoved = 'heavyAdRemoved',
  coreVital = 'coreVital'
}

/**
 * Singleton Export
 * Testing is done in pubSub class which holds all the logic
 * This class just acts as a proxy for defining the interface
 */

class EventManager extends PubSub<typeof Event> {
  events = Event

  constructor(config: { registerGlobalEvents: boolean }) {
    super()

    if (config.registerGlobalEvents) {
      setUpGlobalListeners()
    }
  }

  on(
    event: Event.bidReady,
    cb: (auctionResultsSet: Array<IAuctionResults>) => void
  ): Function
  on(
    event: Event.bidResponse | Event.bidWon,
    cb: (bidResponse: IBidResponse, slot?: Slot) => void
  ): Function
  on(event: Event.bidRequested, cb: (bidRequest: IBidRequest) => void): Function
  on(
    event: Event.bidTimeout,
    cb: (bidRequests: Array<IBidRequest>) => void
  ): Function
  on(event: Event.auctionEnd, cb: (auctionEnd: IAuctionEnd) => void): Function
  on(
    event: Event.slotRenderEnded,
    cb: (event: googletag.events.SlotRenderEndedEvent, slot?: Slot) => void
  ): Function
  on(
    event: Event.slotVisibilityChanged,
    cb: (visibility: number, slot?: Slot) => void
  ): Function
  on(event: Event.impressionViewable, cb: (slot: Slot) => void): Function
  on(event: Event.refreshSlot, cb: (slot: Slot) => void): Function
  on(event: Event.shortCircuitRefresh, cb: (slot: Slot) => void): Function
  on(event: Event.slotRefreshed, cb: (slot: Slot) => void): Function
  on(event: Event.slotWrapperRenderEnded, cb: (slot: Slot) => void): Function
  on(event: Event.initLauncher, cb: (config: IVideoConfig) => void): Function
  on(
    event: Event.outstreamAdError,
    cb: (data: {
      adError: google.ima.AdError
      bidResponse: IBidResponse
      outstreamFloor: number
    }) => void
  ): Function
  on(
    event: Event.heavyAdRemoved,
    cb: (data: IReportingObserverReport) => void
  ): Function
  on(event: Event.coreVital, cb: (data: Metric) => void): Function
  on(event: Event, cb): Function {
    return super.on(event, cb)
  }

  trigger(
    event: Event.bidReady,
    eventObject: Array<IAuctionResults>
  ): EventManager
  trigger(
    event: Event.bidResponse | Event.bidWon,
    eventObject: IBidResponse,
    slot?: Slot
  ): EventManager
  trigger(event: Event.bidRequested, eventObject: IBidRequest): EventManager
  trigger(
    event: Event.bidTimeout,
    eventObject: Array<IBidRequest>
  ): EventManager
  trigger(event: Event.auctionEnd, auctionEnd: IAuctionEnd): EventManager
  trigger(
    event: Event.slotRenderEnded,
    eventObject: googletag.events.SlotRenderEndedEvent,
    slot?: Slot
  ): EventManager
  trigger(
    event: Event.slotVisibilityChanged,
    visibility: number,
    slot?: Slot
  ): EventManager
  trigger(event: Event.refreshSlot, slot?: Slot): EventManager
  trigger(event: Event.shortCircuitRefresh, slot?: Slot): EventManager
  trigger(event: Event.slotRefreshed, slot?: Slot): EventManager
  trigger(event: Event.impressionViewable, slot?: Slot): EventManager
  trigger(event: Event.slotWrapperRenderEnded, slot: Slot): EventManager
  trigger(event: Event.initLauncher, config: IVideoConfig): EventManager
  trigger(
    event: Event.outstreamAdError,
    data: {
      adError: google.ima.AdError
      bidResponse: IBidResponse
      outstreamFloor: number
    }
  ): EventManager
  trigger(
    event: Event.heavyAdRemoved,
    report: IReportingObserverReport
  ): EventManager
  trigger(event: Event.coreVital, metric: Metric): EventManager
  trigger(event: Event, ...args): EventManager {
    return super.trigger(event, ...args)
  }
}

export default new EventManager({ registerGlobalEvents: true })
